import { useOne } from '@refinedev/core';
import { Form, SelectProps } from 'antd';
import { FormProps } from 'antd/lib';
import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router';

import useFormInitialSet from '@/hooks/useFormInitialSet';
import { useSelectPropsWithInitial } from '@/hooks/useSelectWithInitialValue';
import { Ticket } from '@/model/Ticket/Ticket';
import { RESOURCES } from '@/Resources';

export const useShipmentForm = (formProps: FormProps, initialValuesModified, edit?: boolean) => {
  const { form } = formProps;
  const { initialValuesSetted } = useFormInitialSet(formProps.form, initialValuesModified, [], {
    withoutInitial: !edit && !initialValuesModified?.ticket,
  });
  const { companySelectProps, technicianSelectProps, shippersSelectProps, ticketsSelectProps } =
    useOutletContext<{
      companySelectProps: SelectProps;
      shippersSelectProps: SelectProps;
      technicianSelectProps: SelectProps;
      ticketsSelectProps: SelectProps;
    }>();
  const companySelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: companySelectProps,
    resource: 'empresas',
    initialValue:
      initialValuesModified?.company != initialValuesModified?.shipment_payment_company
        ? [initialValuesModified?.company, initialValuesModified?.shipment_payment_company]
        : initialValuesModified?.company,
    labelKey: 'full_name',
    valueKey: 'id',
  });
  const technicianSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: technicianSelectProps,
    resource: 'empresas',
    initialValue: initialValuesModified?.technician,
    labelKey: 'full_name',
    valueKey: 'id',
  });
  const shippersSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: shippersSelectProps,
    resource: RESOURCES.SHIPPERS,
    initialValue: initialValuesModified?.shipper,
    labelKey: 'name',
    valueKey: 'id',
  });
  const ticketsSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: ticketsSelectProps,
    resource: RESOURCES.TICKETS,
    initialValue: initialValuesModified?.ticket,
    labelKey: 'title',
    valueKey: 'id',
  });
  const [movilLink, setMovilLink] = useState<string>('');
  /**
   * Seteo automatico de empresa
   */
  const [selectedTicketOption, setSelectedTicketOption] = useState<Ticket>();

  useEffect(() => {
    if (initialValuesModified?.ticket) {
      if (ticketsSelectPropsWithInitials.options.length > 0) {
        const index = ticketsSelectPropsWithInitials.options.findIndex(
          option => option.value === initialValuesModified?.ticket,
        );

        if (index !== -1) {
          setSelectedTicketOption(ticketsSelectPropsWithInitials.options[index].item);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValuesModified?.ticket]);

  // busqueda de la empresa del ticket
  const { result } = useOne({
    resource: 'empresas',
    id: selectedTicketOption?.empresa,
    queryOptions: {
      enabled: !!selectedTicketOption?.empresa,
    },
  });
  // Se arma la opcion de empresa del ticket
  const ticketCompanyOption = useMemo(
    () =>
      result
        ? [
            {
              label: result.full_name,
              value: result.id,
              item: result,
            },
          ]
        : [],
    [result],
  );

  useEffect(() => {
    if (selectedTicketOption && ticketCompanyOption.length > 0 && !edit) {
      form?.setFieldValue('company', ticketCompanyOption[0].value);
      form?.setFieldValue('shipment_cuit', ticketCompanyOption[0].item?.cuit);

      form?.setFieldValue('shipment_payment_company', ticketCompanyOption[0].value);
      form?.setFieldValue('shipment_payment_cuit', ticketCompanyOption[0].item?.cuit);

      setMovilLink(selectedTicketOption?.movil_link || '');
      void formProps.form?.validateFields(['shipment_payment_company', 'shipment_payment_cuit']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicketOption, ticketCompanyOption]);

  const ticketsSelectPropsModified = {
    ...ticketsSelectPropsWithInitials,
    onChange: (v, option: any) => {
      ticketsSelectProps?.onChange?.(v, option);
      setSelectedTicketOption(option?.item as Ticket);
    },
  };
  const companySelectPropsModified = useMemo(
    () => ({
      ...companySelectPropsWithInitials,
      options: [
        ...ticketCompanyOption,
        ...(companySelectPropsWithInitials.options?.filter(
          c => c.value != ticketCompanyOption[0]?.value,
        ) || []),
      ],
    }),
    [companySelectPropsWithInitials, ticketCompanyOption],
  );
  /**
   *  Chequeo de bloqueo preventivo.
   */
  const [blockPreventive, setBlockPreventive] = useState(false);
  const selectedPaymentCompany = Form.useWatch('shipment_payment_company', form);
  const selectedCompany = Form.useWatch('company', form);

  useEffect(() => {
    if (selectedPaymentCompany || selectedCompany) {
      const selectedPaymentCompanyOption = companySelectPropsModified.options.find(
        option => option.value === selectedPaymentCompany,
      )?.item;
      const selectedCompanyOption = companySelectPropsModified.options.find(
        option => option.value === selectedCompany,
      )?.item;

      setBlockPreventive(
        !!selectedPaymentCompanyOption?.bloqueo_envio_preventivo ||
          !!selectedCompanyOption?.bloqueo_envio_preventivo,
      );
    } else {
      setBlockPreventive(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentCompany, selectedCompany]);

  return {
    shippersSelectProps: shippersSelectPropsWithInitials,
    companySelectProps: companySelectPropsModified,
    ticketsSelectProps: ticketsSelectPropsModified,
    technicianSelectProps: technicianSelectPropsWithInitials,
    initialValuesSetted,
    blockPreventive,
    movilLink,
  };
};
